import { ActionKind, CartState, FxKind, GameMode, heroDef, type HeroId, Phase, type SimEvent, Slot, STATUS_BITS, type Vec3 } from "@rivalwatch/shared";
import { CLIENT_HEROES } from "../heroes/index.ts";
import { Announcer } from "./announcer.ts";
import { prewarm } from "./bake.ts";
import { hotCues } from "./catalog.ts";
import { audio, type AudioEngine, type Loop, type LoopVoice, type Out, type Placement } from "./engine.ts";
import * as fb from "./cues/feedback.ts";
import * as loops from "./cues/loops.ts";
import * as mt from "./cues/match.ts";
import * as wp from "./cues/weapons.ts";
import { playTheme } from "./theory.ts";
import { VoiceDirector } from "./voices.ts";

export type Rel = "self" | "friend" | "foe";
export type Side = "friend" | "foe" | "none";

/** One player as the director sees them this frame. */
export interface ActorAudio {
  id: number;
  rel: Rel;
  hero: HeroId;
  form: number;
  alive: boolean;
  /** Feet. */
  pos: Vec3;
  grounded: boolean;
  crouch: boolean;
  action: number;
  shots: number;
  ultActive: boolean;
  status: number;
  /** Replicated hero vars v0..v3. */
  v: number[];
}

export interface SelfAudio extends ActorAudio {
  vy: number;
  hp: number;
  armor: number;
  shield: number;
  maxHp: number;
  /** Ultimate charge 0..1. */
  ult: number;
  ammo: number;
  /** In the air holding jump with fuel: Anvil's levitation, Mender's flight. */
  lifting: boolean;
}

export interface MatchAudio {
  phase: Phase;
  phaseSec: number;
  round: number;
  /** [us, them] */
  score: [number, number];
  progress: [number, number];
  owner: Side;
  /** 0..1 */
  capture: number;
  capTeam: Side;
  contested: boolean;
  overtime: boolean;
  winner: Side;
  roundsToWin: number;
  point: Vec3;
  mode: GameMode;
  convoy?: ConvoyAudio;
}

export interface ConvoyAudio {
  attacking: boolean;
  cart: Vec3;
  state: CartState;
  /** 0 = still, 1 = full push. */
  speed: number;
  checkpoint: number;
  /** 0..1 along the track. */
  frac: number;
}

export interface ProjectileAudio {
  id: number;
  kind: number;
  owner: number;
  pos: Vec3;
  vel: Vec3;
}

export interface ZoneAudio {
  id: number;
  kind: number;
  pos: Vec3;
}

export interface ConstructAudio {
  id: number;
  kind: number;
  owner: number;
  ownerHero: HeroId;
  rel: Rel | "none";
  pos: Vec3;
}

/** What the director needs from the game. */
export interface AudioHost {
  readonly me: number;
  relOf(netId: number): Rel | "none";
  posOf(netId: number): Vec3 | undefined;
  los(a: Vec3, b: Vec3): boolean;
}

/**
 * The loudness ladder, as linear gains at the placement. Enemy information sits above ally
 * information, and your own constant noise (rifle, feet) sits lowest so it never fatigues.
 */
const LEVEL = {
  fire: { self: 0.45, friend: 0.5, foe: 0.8 },
  step: { self: 0.25, friend: 0.35, foe: 1 },
  ability: { self: 0.65, friend: 0.7, foe: 0.9 },
  theme: { self: 0.7, friend: 0.42, foe: 1 },
  explosion: 0.95,
  hit: 0.5,
  crit: 0.7,
  ko: 0.85,
  hurt: 0.75,
  stinger: 0.85,
};

const STRIDE = { light: 1.05, mid: 1.35, heavy: 1.55 };

interface Track {
  pos?: Vec3;
  stride: number;
  side: number;
  grounded: boolean;
  air: number;
  minVy: number;
  vy: number;
  action: number;
  shots: number;
  alive: boolean;
  status: number;
  hero: HeroId;
  v: number[];
  form: number;
  loops: Map<string, Loop>;
  frame: number;
}

const dist = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const up = (p: Vec3, h: number): Vec3 => ({ x: p.x, y: p.y + h, z: p.z });
const rising = (now: number, before: number, bit: number) => (now & bit) !== 0 && (before & bit) === 0;
/** Closest point to `p` on segment a→b. */
function closest(a: Vec3, b: Vec3, p: Vec3): Vec3 {
  const d = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
  const l2 = d.x * d.x + d.y * d.y + d.z * d.z || 1;
  const k = Math.max(0, Math.min(1, ((p.x - a.x) * d.x + (p.y - a.y) * d.y + (p.z - a.z) * d.z) / l2));
  return { x: a.x + d.x * k, y: a.y + d.y * k, z: a.z + d.z * k };
}

/**
 * Turns game state into sound: diffs every actor, the objective and the match each frame,
 * reacts to sim events, and keeps continuous voices (jets, wards, zones, capture) alive.
 */
export class AudioDirector {
  readonly announcer: Announcer;
  readonly voices: VoiceDirector;
  private tracks = new Map<number, Track>();
  private world = new Map<string, Loop>();
  private frameNo = 0;
  private lastCast = new Map<string, number>();
  private combo = { hit: 0, hitAt: 0, heal: 0, healAt: 0 };
  private streak = { n: 0, at: 0 };
  private me?: { hero: HeroId; form: number; hp: number; armor: number; shield: number; ult: number; alive: boolean; ammo: number };
  private lastMatch?: MatchAudio;
  private near = { us: false, them: false };
  private nearEnd = false;
  private seenZones = new Set<number>();
  private seenConstructs = new Map<number, ConstructAudio>();
  private orbs = new Map<number, { v: Vec3; bounces: number }>();
  private nextMachinery = 0;
  private warmed = false;

  constructor(
    private readonly host: AudioHost,
    private readonly engine: AudioEngine = audio,
  ) {
    this.announcer = new Announcer(engine);
    this.announcer.preload();
    this.voices = new VoiceDirector(engine);
    this.voices.bindAnnouncer(() => this.announcer.speaking);
  }

  // ------------------------------------------------------------------ per frame

  frame(dt: number, me: SelfAudio | undefined, others: ActorAudio[], match: MatchAudio | undefined, projectiles: ProjectileAudio[], zones: ZoneAudio[], constructs: ConstructAudio[] = []) {
    this.frameNo++;
    // Bake the busy cues once there's a context (its sample rate is what we bake at).
    if (!this.warmed && this.engine.ctx) {
      this.warmed = true;
      void prewarm(hotCues()).then(() => loops.warmOvertime(this.engine.ctx!.sampleRate));
    }
    const present = new Set<HeroId>();
    if (me) {
      present.add(me.hero);
      this.selfFrame(dt, me);
    }
    for (const a of others) this.actor(dt, a);
    for (const a of others) present.add(a.hero);
    this.voices.preloadMatch(present);
    for (const [id, t] of this.tracks) {
      if (t.frame === this.frameNo) continue;
      for (const l of t.loops.values()) l.stop(0.2);
      this.tracks.delete(id);
    }
    this.projectiles(projectiles);
    this.zones(zones);
    this.constructs(constructs);
    if (match) this.matchFrame(match);
    this.ambience();
  }

  private track(a: ActorAudio): Track {
    let t = this.tracks.get(a.id);
    if (!t || t.hero !== a.hero) {
      if (t) for (const l of t.loops.values()) l.stop(0.1);
      t = {
        stride: 0,
        side: 0,
        grounded: a.grounded,
        air: 0,
        minVy: 0,
        vy: 0,
        action: a.action,
        shots: a.shots,
        alive: a.alive,
        status: a.status,
        hero: a.hero,
        v: a.v,
        form: a.form,
        loops: new Map(),
        frame: 0,
      };
      this.tracks.set(a.id, t);
    }
    t.frame = this.frameNo;
    return t;
  }

  /** Where an actor's sound comes from: 2D for yourself, spatial (and maybe occluded) for others. */
  private from(a: ActorAudio, gain: number, opts: Partial<Placement> = {}): Placement {
    if (a.rel === "self") return { bus: "sfx", gain, pan: opts.pan ?? 0, priority: opts.priority ?? 6, delay: opts.delay };
    const at = up(a.pos, 1.3);
    const L = this.engine.listener.pos;
    const occluded = dist(at, L) > 4 && !this.host.los(L, at);
    // Explicit fields, not a spread: an `undefined` in opts must not erase a default.
    return { at, gain, occluded, ref: opts.ref ?? 3.5, range: opts.range ?? 80, priority: opts.priority ?? (a.rel === "foe" ? 6 : 4), delay: opts.delay, wet: opts.wet };
  }

  private hold(loopsOf: Map<string, Loop>, key: string, want: boolean, place: () => Placement, build: (o: Out) => LoopVoice, pos?: Vec3, v?: number) {
    let l = loopsOf.get(key);
    if (want) {
      if (!l || !l.alive) {
        l = this.engine.loop(place(), build);
        if (l) loopsOf.set(key, l);
        else loopsOf.delete(key);
      }
      if (l && pos) l.move(pos);
      if (l && v !== undefined) l.set(v);
    } else if (l) {
      l.stop(0.25);
      loopsOf.delete(key);
    }
  }

  /** Everything shared by you and other players: feet, weapons, handling, hero loops, statuses. */
  private actor(dt: number, a: ActorAudio, vy?: number) {
    const t = this.track(a);
    const hero = CLIENT_HEROES[a.hero];
    const w = fb.weightOf(a.hero);
    const L = this.engine.listener.pos;
    const self = a.rel === "self";
    const suppressMovementAudio = a.rel === "foe" && (a.status & STATUS_BITS.stealth) !== 0;

    if (a.alive && t.alive && t.pos && !suppressMovementAudio) {
      const dx = a.pos.x - t.pos.x;
      const dz = a.pos.z - t.pos.z;
      const d = Math.hypot(dx, dz);
      const v = vy ?? (dt > 0 ? (a.pos.y - t.pos.y) / dt : 0);
      t.vy = vy ?? t.vy * 0.5 + v * 0.5;
      if (d < 3 && a.grounded && d / Math.max(dt, 1e-3) > 1.1) {
        t.stride += d;
        const len = STRIDE[w] * (a.crouch ? 0.72 : 1);
        if (t.stride >= len) {
          t.stride = 0;
          t.side ^= 1;
          const near = self || dist(a.pos, L) < 34;
          if (near) {
            const lv = LEVEL.step[a.rel] * (a.crouch ? 0.45 : 1);
            this.engine.play(self ? { bus: "sfx", gain: lv, priority: 3 } : { ...this.from(a, lv), at: up(a.pos, 0.1), ref: 2.5, range: 34, priority: a.rel === "foe" ? 5 : 2 }, fb.footstep(w, a.rel === "foe", t.side));
          }
        }
      }
      if (!a.grounded) {
        t.air += dt;
        t.minVy = Math.min(t.minVy, t.vy);
      }
      if (a.grounded && !t.grounded && t.air > 0.2) this.engine.play(this.from(a, LEVEL.step[a.rel] * 1.6, { priority: 4 }), fb.land(w, -t.minVy));
      if (!a.grounded && t.grounded && t.vy > 1.5) this.engine.play(this.from(a, LEVEL.step[a.rel] * 1.3, { priority: 2 }), fb.jump(w));
      if (a.grounded) {
        t.air = 0;
        t.minVy = 0;
      }
    }

    // Weapon fire: the shot counter ticks for every trigger pull (all heroes, all modes).
    const mod = self ? 65536 : 256;
    const shots = (a.shots - t.shots + mod) % mod;
    if (a.alive && shots > 0 && shots < 8) {
      const cue = hero.audio.fire({ alt: a.action === ActionKind.AltFire, ult: a.ultActive, form: a.form }).cue;
      for (let i = 0; i < Math.min(shots, 2); i++) this.engine.play(this.from(a, LEVEL.fire[a.rel], { pan: self ? 0.12 : undefined, delay: i * 0.05, priority: self ? 7 : undefined }), cue);
    }
    t.shots = a.shots;

    // Handling: reload and melee start.
    if (a.alive && a.action !== t.action) {
      if (a.action === ActionKind.Reload) {
        const seconds = heroDef(a.hero, a.form).reloadTime;
        const cue = hero.audio.reload?.(seconds);
        if (cue && (self || dist(a.pos, L) < 24)) this.engine.play(this.from(a, self ? 0.45 : 0.55, { range: 24, priority: 3 }), cue);
      }
      if (a.action === ActionKind.Melee) this.engine.play(this.from(a, 0.6), wp.melee);
    }
    t.action = a.action;

    // Hero state transitions.
    for (const cue of hero.audio.transitions?.(t.v, a.v) ?? []) this.engine.play(this.from(a, 0.7), cue);
    t.v = a.v.slice();

    // Continuous hero voices.
    const here = self ? undefined : up(a.pos, 1.2);
    const place = (g: number) => () => (self ? { bus: "sfx" as const, gain: g, priority: 7 } : { at: here, gain: g, ref: 4, range: 50, priority: a.rel === "foe" ? 7 : 5 });
    const alive = a.alive;
    const wanted = new Set<string>();
    for (const l of hero.audio.loops?.({ v: a.v, ultActive: a.ultActive, self, alive, form: a.form }) ?? []) {
      const key = `hero:${l.key}`;
      wanted.add(key);
      this.hold(t.loops, key, alive, place(self ? l.gain * 0.65 : l.gain), l.build, here, l.value);
    }
    for (const [key, loop] of t.loops) {
      if (!key.startsWith("hero:") || wanted.has(key)) continue;
      loop.stop(0.25);
      t.loops.delete(key);
    }

    // Statuses landing on other players (yours are handled with the rest of your state).
    if (!self && a.alive) {
      const at = up(a.pos, 1.5);
      if (rising(a.status, t.status, STATUS_BITS.sleep)) this.engine.play({ at, gain: 0.8, range: 40, priority: 6 }, wp.lullaby);
      if (rising(a.status, t.status, STATUS_BITS.linked)) this.engine.play({ at, gain: 0.5, range: 40, priority: 4 }, fb.status("linked"));
      if (rising(a.status, t.status, STATUS_BITS.boost) && a.status & STATUS_BITS.resist) this.engine.play({ at, gain: 0.7, range: 40, priority: 6 }, wp.surged);
    }
    t.status = a.status;
    t.alive = a.alive;
    t.grounded = a.grounded;
    t.pos = a.pos;
    t.form = a.form;
  }

  private selfFrame(dt: number, me: SelfAudio) {
    const before = this.tracks.get(me.id);
    const wasStatus = before?.status ?? me.status;
    this.actor(dt, me, me.vy);
    const t = this.tracks.get(me.id)!;
    const lift = CLIENT_HEROES[me.hero].audio.selfLift?.({ v: me.v, ultActive: me.ultActive, alive: me.alive });
    this.hold(t.loops, "lift", me.alive && me.lifting && !!lift, () => ({ bus: "sfx", gain: 0.35, priority: 6 }), lift ?? loops.jets);

    const prev = this.me;
    this.me = { hero: me.hero, form: me.form, hp: me.hp, armor: me.armor, shield: me.shield, ult: me.ult, alive: me.alive, ammo: me.ammo };
    // A hero swap resets health, ammo and ult: that's not damage, an empty magazine or a charge.
    if (!prev || prev.hero !== me.hero) {
      if (me.alive) this.voices.play({ hero: me.hero, trigger: "spawn", speaker: me.id, rel: "self", form: me.form });
      return;
    }

    // The magazine just ran dry: a hollow click under the last shot says "reload".
    if (me.alive && prev.ammo >= 1 && me.ammo < 1 && heroDef(me.hero, me.form).ammo > 0) this.engine.play({ bus: "sfx", gain: 0.55, priority: 6, delay: 0.06 }, wp.dryFire);

    if (prev.alive && !me.alive) {
      this.engine.play({ bus: "feedback", gain: 0.8, priority: 9 }, fb.death);
      this.streak.n = 0;
    }
    if (!prev.alive && me.alive) {
      this.engine.play({ bus: "feedback", gain: 0.6, priority: 8 }, fb.respawn);
      this.voices.play({ hero: me.hero, trigger: "respawn", speaker: me.id, rel: "self", form: me.form, chance: 0.4 });
    }

    // Damage taken, voiced by the layer that took it.
    if (prev.alive && me.alive) {
      const drops: [fb.Layer, number][] = [
        ["shield", prev.shield - me.shield],
        ["armor", prev.armor - me.armor],
        ["health", prev.hp - me.hp],
      ];
      const [layer, amount] = drops.reduce((a, b) => (b[1] > a[1] ? b : a));
      if (amount > 0.5 && this.engine.throttle("hurt", 0.065)) this.engine.play({ bus: "feedback", gain: LEVEL.hurt, priority: 8 }, fb.hurt(layer, amount / 90));
    }

    if (me.alive && prev.ult < 1 && me.ult >= 1) this.engine.play({ bus: "feedback", gain: 0.55, priority: 8 }, fb.ultReady(me.hero));

    // Your own statuses.
    if (me.alive) {
      const s = me.status;
      if (rising(s, wasStatus, STATUS_BITS.stun) || rising(s, wasStatus, STATUS_BITS.knockdown)) this.engine.play({ bus: "feedback", gain: 0.6 }, fb.status("stun"));
      if (rising(s, wasStatus, STATUS_BITS.sleep)) this.engine.play({ bus: "feedback", gain: 0.6 }, fb.status("sleep"));
      if (rising(s, wasStatus, STATUS_BITS.antiHeal)) this.engine.play({ bus: "feedback", gain: 0.55 }, fb.status("antiHeal"));
      if (rising(s, wasStatus, STATUS_BITS.linked)) this.engine.play({ bus: "feedback", gain: 0.55 }, fb.status("linked"));
      if (rising(s, wasStatus, STATUS_BITS.boost)) this.engine.play({ bus: "feedback", gain: 0.6 }, s & STATUS_BITS.resist ? wp.surged : fb.status("boost"));
    }

    // Low health: a heartbeat, and the world closing in.
    const ratio = (me.hp + me.armor + me.shield) / Math.max(1, me.maxHp);
    const prevRatio = (prev.hp + prev.armor + prev.shield) / Math.max(1, me.maxHp);
    if (me.alive && ratio < 0.3 && prevRatio >= 0.3) this.voices.play({ hero: me.hero, trigger: "low_hp", speaker: me.id, rel: "self", form: me.form });
    const low = me.alive && ratio < 0.35;
    const urgency = low ? 1 - ratio / 0.35 : 0;
    this.hold(t.loops, "heart", low, () => ({ bus: "feedback", gain: 0.7, priority: 8 }), loops.heartbeat, undefined, urgency);
    const muffle = !me.alive ? 480 : me.status & STATUS_BITS.sleep ? 900 : low ? 2400 + 17600 * (1 - urgency) ** 2 : 20000;
    this.engine.setMuffle(muffle, !me.alive ? 0.15 : 0.25);
  }

  // ------------------------------------------------------------------ events

  event(e: SimEvent, predicted: boolean) {
    const me = this.host.me;
    const L = this.engine.listener.pos;
    switch (e.type) {
      case "tracer": {
        if (predicted) return;
        if (e.kind === FxKind.Hook) {
          this.engine.play({ at: e.to, gain: 0.9, range: 60, priority: 7 }, wp.hookHit);
          return;
        }
        if (e.owner === me) return;
        const c = closest(e.from, e.to, L);
        const d = dist(c, L);
        if (this.host.relOf(e.owner) === "foe" && d < 2.4 && dist(e.to, L) > 1.2 && this.engine.throttle("whiz", 0.07)) this.engine.play({ at: c, gain: 0.55, ref: 1.2, range: 4, priority: 6, wet: 0.05 }, wp.whiz);
        if (dist(e.to, L) < 22 && this.engine.throttle(`ric:${e.owner}`, 0.09)) this.engine.play({ at: e.to, gain: 0.35, ref: 2, range: 22, priority: 2 }, wp.ricochet);
        return;
      }
      case "impact":
        if (!predicted) this.engine.play({ at: e.pos, gain: 0.6, range: 40, priority: 4 }, wp.impact(e.kind));
        return;
      case "explosion":
        if (!predicted) this.engine.play({ at: e.pos, gain: LEVEL.explosion, ref: 5, range: 130, wet: 0.35, priority: 8 }, wp.explosion(e.kind, e.radius));
        return;
      case "damage": {
        if (predicted || e.attacker !== me || e.target === me) return;
        const now = performance.now() / 1000;
        this.combo.hit = now - this.combo.hitAt < 0.35 ? this.combo.hit + 1 : 0;
        this.combo.hitAt = now;
        if (this.engine.throttle("hit", e.crit ? 0.02 : 0.03)) this.engine.play({ bus: "feedback", gain: e.crit ? LEVEL.crit : LEVEL.hit, priority: 8 }, e.crit ? fb.critTick : fb.hitTick(this.combo.hit));
        return;
      }
      case "heal": {
        if (predicted) return;
        const now = performance.now() / 1000;
        if (e.healer === me && e.target !== me) {
          this.combo.heal = now - this.combo.healAt < 1.2 ? this.combo.heal + 1 : 0;
          this.combo.healAt = now;
          if (this.engine.throttle("heal", 0.09)) this.engine.play({ bus: "feedback", gain: 0.5, priority: 6 }, fb.healTick(this.combo.heal));
        } else if (e.target === me && e.healer !== me && this.engine.throttle("healed", 0.45)) {
          this.engine.play({ bus: "feedback", gain: 0.6, priority: 5 }, fb.healed);
          if (e.amount >= 45 && this.me) this.voices.play({ hero: this.me.hero, trigger: "healed", speaker: me, rel: "self", form: this.me.form });
        }
        return;
      }
      case "kill": {
        if (predicted || e.victim === me) return;
        // Rivals credit: a shared KO is still your KO; enabling it without one is an assist.
        if (e.killer !== me && !e.kos.includes(me)) {
          if (e.assists.includes(me)) this.engine.play({ bus: "feedback", gain: 0.75, priority: 8 }, fb.assist);
          return;
        }
        const now = performance.now() / 1000;
        this.streak.n = now - this.streak.at < 4 ? this.streak.n + 1 : 1;
        this.streak.at = now;
        this.engine.play({ bus: "feedback", gain: LEVEL.ko, priority: 9 }, fb.ko(this.streak.n));
        if (this.me) this.voices.play({ hero: this.me.hero, trigger: this.streak.n >= 2 ? "multikill" : "kill", speaker: me, rel: "self", form: this.me.form, chance: this.streak.n >= 2 ? 1 : 0.6 });
        return;
      }
      case "cast":
        this.cast(e.owner, e.slot, e.hero as HeroId);
        return;
      case "form": {
        if (predicted) return;
        const rel = e.target === me ? "self" : this.host.relOf(e.target);
        if (rel === "none") return;
        const pos = rel === "self" ? undefined : this.host.posOf(e.target);
        const cue = CLIENT_HEROES[e.hero as HeroId].audio.formChange?.[e.form] ?? fb.status("boost");
        this.engine.play(pos ? { at: up(pos, 1.2), gain: LEVEL.ability[rel], ref: 4, range: 70, priority: 6 } : { bus: "sfx", gain: 0.6, priority: 7 }, cue);
        return;
      }
      case "pickup":
        if (!predicted) this.engine.play({ at: e.pos, gain: 0.6, range: 40, priority: 5 }, wp.pickup(e.large));
        return;
      case "revive": {
        if (predicted) return;
        if (e.target === me) {
          this.engine.play({ bus: "feedback", gain: 0.7, priority: 8 }, wp.revive);
          this.announcer.say("revived", 6);
          if (this.me) this.voices.play({ hero: this.me.hero, trigger: "revived", speaker: me, rel: "self", form: this.me.form });
        } else {
          const p = this.host.posOf(e.target);
          if (p) this.engine.play({ at: up(p, 1), gain: 0.7, range: 50, priority: 6 }, wp.revive);
        }
        return;
      }
    }
  }

  private cast(owner: number, slot: number, hero: HeroId) {
    const rel = owner === this.host.me ? "self" : this.host.relOf(owner);
    if (rel === "none") return;
    // The shooter predicts their casts; the server echoes them a round trip later.
    const key = `${owner}:${slot}`;
    const now = performance.now() / 1000;
    if (now - (this.lastCast.get(key) ?? -1e9) < 0.5) return;
    this.lastCast.set(key, now);
    const pos = rel === "self" ? undefined : this.host.posOf(owner);
    const at = pos ? up(pos, 1.3) : undefined;
    const t = this.tracks.get(owner);
    const form = t?.form ?? 0;
    const cue = CLIENT_HEROES[hero].audio.abilityForms?.[form]?.[slot as Slot] ?? CLIENT_HEROES[hero].audio.ability?.[slot as Slot];
    if (cue) this.engine.play(at ? { at, gain: LEVEL.ability[rel], ref: 4, range: 80, priority: rel === "foe" ? 7 : 5 } : { bus: "sfx", gain: LEVEL.ability.self, priority: 7 }, cue);
    const spoke = this.voices.playCast(hero, owner, slot, rel, pos, form);
    if (slot !== Slot.Ultimate) return;
    // The ultimate callout: the hero's theme — heroic for us, hunted for them.
    const pan = at ? this.engine.panOf(at) * 0.6 : 0;
    if (rel === "foe") {
      this.engine.play({ bus: "feedback", gain: LEVEL.theme.foe * (spoke ? 0.5 : 1), pan, priority: 10 }, (o) => playTheme(o, hero, "hostile"));
      this.engine.duck(5, 1.8);
    } else this.engine.play({ bus: "feedback", gain: LEVEL.theme[rel] * (spoke ? 0.5 : 1), pan, priority: 9 }, (o) => playTheme(o, hero, "heroic"));
  }

  // ------------------------------------------------------------------ world objects

  private projectiles(list: ProjectileAudio[]) {
    const L = this.engine.listener.pos;
    const seen = new Set<number>();
    for (const p of list) {
      seen.add(p.id);
      const bounceCue = Object.values(CLIENT_HEROES).find((h) => h.audio.projectileBounces?.[p.kind])?.audio.projectileBounces?.[p.kind];
      if (bounceCue) {
        const was = this.orbs.get(p.id);
        const sp = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
        let bounces = was?.bounces ?? 0;
        if (was && sp > 2) {
          const ws = Math.hypot(was.v.x, was.v.y, was.v.z) || 1;
          const cos = (was.v.x * p.vel.x + was.v.y * p.vel.y + was.v.z * p.vel.z) / (ws * sp);
          if (cos < 0.6) this.engine.play({ at: p.pos, gain: 0.55, range: 35, priority: 3 }, bounceCue(++bounces));
        }
        this.orbs.set(p.id, { v: p.vel, bounces });
      }
    }
    for (const id of this.orbs.keys()) if (!seen.has(id)) this.orbs.delete(id);
    const flightOf = (kind: number) => Object.values(CLIENT_HEROES).find((h) => h.audio.flight?.[kind])?.audio.flight?.[kind];
    const nearest = list
      .filter((p) => flightOf(p.kind))
      .map((p) => ({ p, d: dist(p.pos, L) }))
      .filter((x) => x.d < 32)
      .sort((a, b) => a.d - b.d)
      .slice(0, 6);
    const want = new Set(nearest.map((x) => `p${x.p.id}`));
    for (const [k, l] of this.world) {
      if (k.startsWith("p") && !want.has(k)) {
        l.stop(0.08);
        this.world.delete(k);
      }
    }
    for (const { p } of nearest) this.hold(this.world, `p${p.id}`, true, () => ({ at: p.pos, gain: 0.7, ref: 2.5, range: 40, priority: 3 }), flightOf(p.kind)!, p.pos);
  }

  private zones(list: ZoneAudio[]) {
    const L = this.engine.listener.pos;
    const seen = new Set<number>();
    for (const z of list) {
      seen.add(z.id);
      if (!this.seenZones.has(z.id)) {
        const cue = Object.values(CLIENT_HEROES).find((h) => h.audio.zoneStarts?.[z.kind])?.audio.zoneStarts?.[z.kind];
        if (cue) this.engine.play({ at: up(z.pos, 1), gain: 0.6, range: 50, priority: 5 }, cue);
      }
      const spec = Object.values(CLIENT_HEROES).find((h) => h.audio.zoneLoops?.[z.kind])?.audio.zoneLoops?.[z.kind];
      if (spec) this.hold(this.world, `z${z.id}`, dist(z.pos, L) < spec.range, () => ({ at: up(z.pos, 0.6), gain: spec.gain, ref: 3, range: spec.range + 6, priority: 4 }), spec.build, up(z.pos, 0.6));
    }
    for (const [k, l] of this.world) {
      if (k.startsWith("z") && !seen.has(Number(k.slice(1)))) {
        l.stop(0.4);
        this.world.delete(k);
      }
    }
    this.seenZones = seen;
  }

  private constructs(list: ConstructAudio[]) {
    const L = this.engine.listener.pos;
    const current = new Map<number, ConstructAudio>();
    for (const c of list) {
      current.set(c.id, c);
      const hero = CLIENT_HEROES[c.ownerHero];
      if (!this.seenConstructs.has(c.id)) {
        const cue = hero.audio.constructSpawned?.[c.kind];
        if (cue) this.engine.play({ at: up(c.pos, 0.8), gain: 0.55, range: 45, priority: c.rel === "foe" ? 6 : 4 }, cue);
      }
      const spec = hero.audio.constructLoops?.[c.kind];
      if (spec) this.hold(this.world, `c${c.id}`, c.rel !== "none" && dist(c.pos, L) < spec.range, () => ({ at: up(c.pos, 0.8), gain: spec.gain, ref: 3, range: spec.range + 6, priority: c.rel === "foe" ? 5 : 3 }), spec.build, up(c.pos, 0.8));
    }
    for (const [id, prev] of this.seenConstructs) {
      if (current.has(id)) continue;
      const l = this.world.get(`c${id}`);
      if (l) {
        l.stop(0.2);
        this.world.delete(`c${id}`);
      }
      const cue = CLIENT_HEROES[prev.ownerHero].audio.constructDestroyed?.[prev.kind];
      if (cue) this.engine.play({ at: up(prev.pos, 0.8), gain: 0.65, range: 55, priority: prev.rel === "foe" ? 6 : 4 }, cue);
    }
    this.seenConstructs = current;
  }

  // ------------------------------------------------------------------ the match

  private matchFrame(m: MatchAudio) {
    const p = this.lastMatch;
    this.lastMatch = { ...m, score: [...m.score], progress: [...m.progress], convoy: m.convoy && { ...m.convoy, cart: { ...m.convoy.cart } } };
    const cv = m.convoy;
    const pc = p?.convoy;
    const inProgress = m.phase === Phase.InProgress;
    const music = (g: number, pr = 9): Placement => ({ bus: "music", gain: g, priority: pr });

    if (p && m.phase !== p.phase) {
      if (m.phase === Phase.Setup) {
        this.near = { us: false, them: false };
        this.nearEnd = false;
        const final = !cv && m.score[0] === m.roundsToWin - 1 && m.score[1] === m.roundsToWin - 1;
        this.engine.play(music(LEVEL.stinger), mt.roundIntro);
        this.announcer.say(final ? "round_final" : m.round <= 1 ? "round_1" : m.round === 2 ? "round_2" : "round_3", 8);
        if (cv) this.announcer.say(cv.attacking ? "prepare_attack" : "prepare_defend", 8);
      } else if (inProgress) {
        this.engine.play(music(LEVEL.stinger), mt.unlock);
        this.announcer.say("unlocked", 8);
      } else if (m.phase === Phase.RoundEnd && cv && m.round <= 1) {
        // Convoy's first attack is over: nobody has won yet. Did it get there?
        const delivered = cv.frac >= 1;
        this.engine.play(music(LEVEL.stinger), cv.attacking === delivered ? mt.roundWon : mt.roundLost);
        this.announcer.clear();
        this.announcer.say(delivered ? (cv.attacking ? "delivered" : "enemy_delivered") : "switch_sides", 9);
      } else if (m.phase === Phase.RoundEnd) {
        const won = m.winner === "friend";
        this.engine.play(music(LEVEL.stinger), won ? mt.roundWon : mt.roundLost);
        this.announcer.clear();
        if (cv && cv.frac >= 1) this.announcer.say(cv.attacking ? "delivered" : "enemy_delivered", 9);
        else this.announcer.say(m.winner === "none" ? "draw" : won ? "round_won" : "round_lost", 9);
      } else if (m.phase === Phase.MatchEnd) {
        const won = m.winner === "friend";
        const draw = m.winner === "none";
        this.engine.play(music(1, 10), draw ? mt.roundIntro : won ? mt.victory : mt.defeat);
        this.announcer.clear();
        this.announcer.say(draw ? "draw" : won ? "victory" : "defeat", 10);
        if (!draw && this.me) this.voices.play({ hero: this.me.hero, trigger: won ? "victory" : "defeat", speaker: this.host.me, rel: "self" });
      }
    }
    if (p && m.phase === Phase.Setup && m.phaseSec !== p.phaseSec && m.phaseSec >= 1 && m.phaseSec <= 5) this.engine.play(music(0.7, 8), mt.countdown(m.phaseSec));

    if (p && inProgress && p.phase === Phase.InProgress && cv && pc) {
      if (cv.checkpoint > pc.checkpoint) {
        this.engine.play(music(LEVEL.stinger), cv.attacking ? mt.captured : mt.pointLost);
        this.announcer.say(cv.attacking ? "checkpoint" : "enemy_checkpoint", 7);
      }
      if (cv.state === CartState.Pushing && pc.state !== CartState.Pushing) this.announcer.say(cv.attacking ? "payload_moving" : "enemy_payload_moving", 5, 20);
      if (cv.state === CartState.Rolling && pc.state !== CartState.Rolling && cv.attacking) this.announcer.say("rolling_back", 6, 15);
      if (m.contested && !p.contested) this.announcer.say("contested", 3, 25);
      if (!this.nearEnd && cv.frac >= 0.9) {
        this.nearEnd = true;
        this.announcer.say(cv.attacking ? "payload_near" : "enemy_payload_near", 6);
      }
      if (m.overtime && !p.overtime) this.announcer.say("overtime", 8, 6);
    } else if (p && inProgress && p.phase === Phase.InProgress) {
      if (m.owner !== p.owner) {
        if (m.owner === "friend") {
          this.engine.play(music(LEVEL.stinger), mt.captured);
          this.announcer.say("captured", 7);
        } else if (m.owner === "foe") {
          this.engine.play(music(LEVEL.stinger), mt.pointLost);
          this.announcer.say(p.owner === "friend" ? "lost" : "enemy_captured", 7);
        }
      }
      if (m.capTeam !== p.capTeam && m.capTeam !== "none" && m.capTeam !== m.owner) this.announcer.say(m.capTeam === "friend" ? "capturing" : "enemy_capturing", 5, 10);
      if (m.contested && !p.contested) this.announcer.say("contested", 3, 25);
      if (!this.near.us && m.progress[0] >= 90) {
        this.near.us = true;
        this.announcer.say("near_win", 6);
      }
      if (!this.near.them && m.progress[1] >= 90) {
        this.near.them = true;
        this.announcer.say("enemy_near_win", 6);
      }
      if (m.overtime && !p.overtime) this.announcer.say("overtime", 8, 6);
    }

    const w = this.world;
    const capturing = inProgress && m.capture > 0 && !m.contested && m.capTeam !== "none" && m.capTeam !== m.owner;
    this.hold(w, "m:ours", capturing && m.capTeam === "friend", () => ({ bus: "music", gain: 0.8, priority: 8 }), loops.capture(true), undefined, m.capture);
    this.hold(w, "m:theirs", capturing && m.capTeam === "foe", () => ({ bus: "music", gain: 0.8, priority: 8 }), loops.capture(false), undefined, m.capture);
    this.hold(w, "m:contested", inProgress && m.contested, () => ({ bus: "music", gain: 0.8, priority: 8 }), loops.contested);
    this.hold(w, "m:point", inProgress && !cv, () => ({ at: up(m.point, 1), gain: 0.55, ref: 7, range: 70, priority: 7, wet: 0.3 }), loops.pointHum, up(m.point, 1), m.owner === "friend" ? 0.5 : m.owner === "foe" ? 1 : 0);
    if (cv) this.hold(w, "m:payload", true, () => ({ at: up(cv.cart, 0.8), gain: 0.55, ref: 6, range: 60, priority: 7, wet: 0.25 }), loops.payload, up(cv.cart, 0.8), cv.speed);
    else this.hold(w, "m:payload", false, () => ({ gain: 0 }), loops.payload);
    const heat = Math.max(m.progress[0], m.progress[1]) / 100;
    this.hold(w, "m:overtime", inProgress && m.overtime, () => ({ bus: "music", gain: 0.65, priority: 9 }), loops.overtime, undefined, heat);
  }

  // ------------------------------------------------------------------ ambience

  private ambience() {
    this.hold(this.world, "amb", true, () => ({ bus: "amb", gain: 0.55, priority: 9 }), loops.roomTone);
    const now = this.engine.now;
    if (now < this.nextMachinery) return;
    this.nextMachinery = now + 6 + Math.random() * 9;
    const L = this.engine.listener.pos;
    const a = Math.random() * Math.PI * 2;
    const r = 30 + Math.random() * 25;
    const at = { x: L.x + Math.cos(a) * r, y: L.y + 3 + Math.random() * 6, z: L.z + Math.sin(a) * r };
    this.engine.play({ at, bus: "amb", gain: 0.55, ref: 8, range: 100, wet: 0.7, priority: 1 }, loops.machinery);
  }

  dispose() {
    for (const t of this.tracks.values()) for (const l of t.loops.values()) l.stop(0.1);
    for (const l of this.world.values()) l.stop(0.3);
    this.tracks.clear();
    this.world.clear();
    this.announcer.clear();
    this.engine.setMuffle(20000, 0.05);
  }
}
